import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faHome, faChartLine, faWrench, faArrowRight } from '@fortawesome/free-solid-svg-icons';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalBridges: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch basic statistics
      const [usersRes, bridgesRes] = await Promise.all([
        api.get('/users'),
        api.get('/bridges')
      ]);

      // Handle different response formats
      const users = Array.isArray(usersRes.data) ? usersRes.data : 
                   (usersRes.data?.users && Array.isArray(usersRes.data.users)) ? usersRes.data.users : [];
      const bridges = Array.isArray(bridgesRes.data) ? bridgesRes.data : 
                     (bridgesRes.data?.data && Array.isArray(bridgesRes.data.data)) ? bridgesRes.data.data : [];

      setStats({
        totalUsers: users.length,
        totalBridges: bridges.length
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Set default values if API calls fail
      setStats({
        totalUsers: 0,
        totalBridges: 0
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-2">Welcome back, {user?.fullName || user?.username}!</p>
        </div>

        {/* Basic KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FontAwesomeIcon icon={faUsers} className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <FontAwesomeIcon icon={faHome} className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Bridges</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalBridges}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <button
              onClick={() => navigate('/enhanced-simulation-results')}
              className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow text-left group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-3 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                    <FontAwesomeIcon icon={faChartLine} className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">Simulation Engine</h3>
                    <p className="text-sm text-gray-600 mt-1">Run simulations and analyze results</p>
                  </div>
                </div>
                <FontAwesomeIcon icon={faArrowRight} className="h-5 w-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
              </div>
            </button>

            <button
              onClick={() => navigate('/repair-projects')}
              className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow text-left group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-3 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
                    <FontAwesomeIcon icon={faWrench} className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">Repair Projects</h3>
                    <p className="text-sm text-gray-600 mt-1">Manage and track repair projects</p>
                  </div>
                </div>
                <FontAwesomeIcon icon={faArrowRight} className="h-5 w-5 text-gray-400 group-hover:text-green-600 transition-colors" />
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;